import React, { Fragment } from "react";
import { useFormikContext } from "formik";
import { STATE_KEYS } from "utils/constants/state";
import get from "lodash/get";
import Toggle from "react-toggle";
import MenuNavHeader from "components/MenuNavHeader";
import ImageField from "components/ImageField";
import { getToggleProps } from "utils/form";
import TextareaController from "components/TextareaController";
import { useCtxReferences, useIsAuthed } from "utils/selectors";
import { interpolateRoute } from "utils/route";
import PATHS from "utils/paths";
import { HUB_APP_SUB_SECTION } from "utils/constants/navigation";
import { SETTINGS_TABS } from "utils/constants/settings";
import Link from "next/link";
import UnauthedAccountPrompt from "components/UnauthedAccountPrompt";

const PresentationSettings = (props) => {
  const {
    values,
    touched,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();

  const isAuthed = useIsAuthed();
  const { application } = useCtxReferences();

  const brandingRoute = `${interpolateRoute(PATHS.HUB_APPLICATIONS_SHOW, {
    application
  })}/${HUB_APP_SUB_SECTION.SETTINGS}?tab=${SETTINGS_TABS.BRANDING}`;

  const name = props.name;
  const configPath = `${name}.config`;
  const rootPath = `${name}.data`;
  const content = get(values, rootPath, {});

  /**
   * Brand header on preview section
   */
  const showBrandPath = `${configPath}.${STATE_KEYS.ENTITY_CONFIG.BRAND_SHOW}`;
  const showBrandTouched = get(touched, showBrandPath);
  const showBrand = Boolean(get(values, showBrandPath));

  const showInitialPreviewPath = `${configPath}.${STATE_KEYS.ENTITY_CONFIG.INITIAL_PREVIEW_SHOW}`;
  const showInitialPreviewTouched = get(touched, showInitialPreviewPath);
  const showInitialPreview = Boolean(get(values, showInitialPreviewPath));

  return (
    <Fragment>
      <MenuNavHeader label="Presentation" />
      {isAuthed && (
        <div className="pt3">
          <div className="f5 ph3 pb3 fw6">Branding</div>
          <div className="ph3 bg-white h-100 bh--inset dib w-100 pb3 bb hairline-1">
            <div className="w-100 flex flex-column">
              <div className="w-100 flex flex-row justify-between items-center">
                <label className="f6 dib fw6">Logo</label>
                <Toggle
                  {...getToggleProps({
                    active: showBrand,
                    setFieldValue: (_, active) => {
                      setFieldValue(showBrandPath, active);
                      if (!showBrandTouched) {
                        setFieldTouched(showBrandPath, true);
                      }
                    }
                  })}
                />
              </div>
              <div className="f6 w-100 lh-copy pt1">
                Show your logo at the top of the page.
              </div>
              {isAuthed && (
                <div className="f6 w-100 lh-copy pt1">
                  Update logo within
                  <Link href={brandingRoute}>
                    <span className="f7 f6-ns fw6 link color-primary link__decoration--hover pointer pl1">
                      brand settings.
                    </span>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="pt3 bh--inset">
        <div className="f5 ph3 pb2 fw6">Initial preview</div>
        <div className="f6 ph3 pb3 lh-copy">
          Shown when no checkout form selections have been made.
        </div>
        <div className="ph3 bg-white h-100 bh--inset dib w-100 pb3">
          <div className="w-100 flex flex-column">
            <div className="w-100 flex flex-row justify-between items-center">
              <label className="f6 dib fw5">Enabled</label>
              <Toggle
                {...getToggleProps({
                  active: showInitialPreview,
                  setFieldValue: (_, active) => {
                    setFieldValue(showInitialPreviewPath, active);
                    if (!showInitialPreviewTouched) {
                      setFieldTouched(showInitialPreviewPath, true);
                    }
                  }
                })}
              />
            </div>
          </div>
        </div>
        {isAuthed ? (
          <ImageField
            name={`${rootPath}.initialPreview.image`}
            image={content.initialPreview && content.initialPreview.image}
            uploadPrompt="Upload an image"
          />
        ) : (
          <UnauthedAccountPrompt
            header="Use your own logo"
            subheader="Sign up to use your own logo, assets and products in your checkout pages."
            customClasses={{
              actionsWrapper: "pt3",
              header: "f5 fw6",
              subheader: "f6 lh-copy",
              container:
                "w-100 flex flex-column justify-between pa3 items-start bg-white bh--inset bt bb hairline-1"
            }}
          />
        )}

        <div className="ph3 bg-white h-100 bh--inset">
          <div className="pt3">
            <TextareaController
              name="title"
              key={`${rootPath}.initialPreview.title`}
              label="Name"
              rootPath={`${rootPath}.initialPreview`}
              fieldKey="title"
              minRows={1}
            />
          </div>
          <div className="pt3">
            <TextareaController
              key={`${rootPath}.initialPreview.subtitle`}
              name="subtitle"
              label="Subtitle"
              rootPath={`${rootPath}.initialPreview`}
              fieldKey="subtitle"
              minRows={1}
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default PresentationSettings;
